import { DatabasesDisplayGridRowData, DatabaseTablesDisplayGridRowData } from '@tableau/data-catalog-client-components';
import { formatValueAsMetricNumber } from '@tableau/metrics-shared-widgets-dom/lib/src/formatting/formatting';
import { SiteUrls } from '@tableau/shared-urls';
import { ActivitySpinnerProps, ActivitySpinnerSize } from '@tableau/widgets';
import * as _ from 'underscore';

import { ProfilePictureStyles } from '../../app/content/styles/ProfilePictureStyles';
import { HeaderIconButton } from '../../app/header/HeaderIconButton';
import { vizHeaderSearchButtonImgTestId } from '../../app/layout/PageHeaderSearch';
import { vizHeaderSearchOpenButtonTestId } from '../../app/layout/VizViewerPageHeader';
import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Formatters, Messages } from '../../codegen/Localize';
import { Location } from '../../common/react/Location';
import { SearchResultClickedEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { getMergedMetricInfo } from '../../metrics';
import { FeatureFlags } from '../FeatureFlags';
import { ListResult, mapListResult } from '../fetchers/ListResult';
import { Filter } from '../helpers/Filter';
import { Keys } from '../helpers/Keys';
import { Order } from '../helpers/Order';
import { ContentFiltersKeys } from '../models/ContentFilters';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IDataSourceInfo } from '../models/IDataSourceInfo';
import { IMetricInfo } from '../models/IMetricInfo';
import { IProjectInfo } from '../models/IProjectInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookInfo } from '../models/IWorkbookInfo';
import { OrderKeys } from '../models/OrderKeys';
import { sortOptionsBySearchSubspace } from '../models/sort/SortOptionDefinitions';
import { Module } from '../Module';
import { Databases } from '../resources/Databases';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { MetricDataPoints } from '../resources/MetricDataPoints';
import { Metrics } from '../resources/Metrics';
import { Projects } from '../resources/Projects';
import { ResourceQuery } from '../resources/ResourceQuery';
import { Tables } from '../resources/Tables';
import { Users } from '../resources/Users';
import { Views } from '../resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { IMetricDataList } from '../ServerApi/types/IMetricDataList';
import { IOrderClause } from '../ServerApi/types/IOrderClause';
import { IUserWithPublicInfo } from '../ServerApi/types/IUser';
import { ServerService } from '../services/ServerService';
import { TelemetryService } from '../services/TelemetryService';

export interface IOmniboxExternalScope extends ng.IScope {
  closed: (event: BaseJQueryEventObject) => void;
  closedWithoutNeedingFocus: (event: BaseJQueryEventObject) => void;
  isSearchOpen: boolean;
}

export interface IMetricInfoForOmni extends IMetricInfo {
  value?: string;
}

export interface CatalogOmniboxItem {
  readonly id: string;
  readonly name: string;
  readonly isCertified: boolean;
  readonly workbookCount: number;
  readonly datasourceCount: number;
}

function mapCatalogItem(item: DatabaseTablesDisplayGridRowData | DatabasesDisplayGridRowData): CatalogOmniboxItem {
  return {
    id: item.id,
    name: item.name,
    isCertified: item.isCertified,
    workbookCount: item.downstreamWorkbooksConnection ? item.downstreamWorkbooksConnection.totalCount : 0,
    datasourceCount: item.downstreamDatasourcesConnection ? item.downstreamDatasourcesConnection.totalCount : 0,
  };
}

export interface IOmniboxScope extends ng.IScope {
  closed: (locals: { event: BaseJQueryEventObject }) => void;
  closedWithoutNeedingFocus: (locals: { event: BaseJQueryEventObject }) => void;

  searchOpen: boolean;
  searchText: string;
  showResults: boolean;
  showNoResults: boolean;
  showPending: boolean;
  showCatalogingError: boolean;
  selectedIndex: number;
  maxResultsPerType: number;
  textBoxName: string;
  userProfilePictureSize: number;
  userProfilePictureFontSize: number;
  activitySpinnerProps: ActivitySpinnerProps;

  datasources: ListResult<IDataSourceInfo>;
  flows: ListResult<FlowListItemInfo>;
  projects: ListResult<IProjectInfo>;
  users: ListResult<IUserWithPublicInfo>;
  views: ListResult<IViewInfo>;
  metrics: ListResult<IMetricInfoForOmni>;
  workbooks: ListResult<IWorkbookInfo>;
  dataRoles: ListResult<IDataRoleInfo>;

  tables: ListResult<CatalogOmniboxItem>;
  databases: ListResult<CatalogOmniboxItem>;
  tablesReturnedError: boolean;
  databasesReturnedError: boolean;

  popupMaxHeight: () => number;
  close: (isInternallyTriggered: boolean, $event?: BaseJQueryEventObject) => any;
  isItemSelected: (type: string, index: number) => boolean;
  sendTelemetryEvent: (type: string, seeAll: boolean, index: number, total: number) => void;
  selectItem: (type: string, index: number) => void;
  itemHref: (state: string, params: any) => string;
  keydown: ($event: JQueryKeyEventObject) => any;
  seeAllResultsMessage: () => string;
  seeAllHref: (state: string, type: string) => string;
  showSeeAll: (list: ListResult<any>, type: string) => boolean;
  tooltipForDataSource: (datasource: IDataSourceInfo) => string;
  tooltipForView: (view: IViewInfo) => string;
  tooltipForWorkbook: (workbook: IWorkbookInfo) => string;
  tooltipForFlow: (flow: FlowListItemInfo) => string;
  clearSearch: () => void;

  selectedId: () => string;
  createItemId: (type: string, index: number) => string;
  totalNumberOfSections: () => number;
}

export class OmniboxCtrl {

  public static MaxResultsPerType = 3;
  public static InputDebounceMilliseconds = 200;
  private static SpaceLeftWhenMaxHeight = 100;
  private static TextBoxName = 'omniboxTextBox';
  private static GetUsersExtraParams = {
    'getItemCounts': true
  };

  private currentRequestProgress: {
    pendingRequests: number;
    totalResults: number;
  };
  private selectedIndex: number;
  private maxSelectedIndex: number;
  private startIndexForType: _.Dictionary<number>;
  private isUnmountingOmni: boolean;

  public static $inject = [
    '$scope',
    '$translate',
    '$window',
    'ServerService',
    'DataSources',
    'DataRoles',
    'Flows',
    'Projects',
    'Users',
    'Views',
    'Metrics',
    'Workbooks',
    'Tables',
    'Databases',
    'TelemetryService',
    'MetricDataPoints'
  ];

  constructor(
    private $scope: IOmniboxScope,
    private $translate: ng.translate.ITranslateService,
    private $window: ng.IWindowService,
    private server: ServerService,
    private datasources: DataSources,
    private dataRoles: DataRoles,
    private flows: Flows,
    private projects: Projects,
    private users: Users,
    private views: Views,
    private metrics: Metrics,
    private workbooks: Workbooks,
    private tables: Tables,
    private databases: Databases,
    private telemetry: TelemetryService,
    private metricDataPointsResource: MetricDataPoints
  ) {
    this.startIndexForType = {};
    this.isUnmountingOmni = false;

    const debouncedUpdate = _.debounce(() => {
      this.$scope.$evalAsync(() => {
        if (!this.isUnmountingOmni) {
          this.updateResults();
        }
      });
    }, OmniboxCtrl.InputDebounceMilliseconds);
    this.$scope.$watch('searchText', () => {
      debouncedUpdate();
    });

    // this handles closing the omnibox by mouse-down clicks and keyboard presses outside the omnibox
    // as well as internal keys and clicks.
    this.$scope.close = (isInternallyTriggered: boolean, $event?: BaseJQueryEventObject) => {
      let isCloseIcon = false;
      if ($event) {
        const targetElement = $($event.target);
        const isTextBox = targetElement.attr('name') === OmniboxCtrl.TextBoxName;
        const isButton = targetElement.closest('#' + HeaderIconButton.searchBoxIconButtonId).length > 0;
        isCloseIcon = targetElement.attr('data-tb-test-id') === vizHeaderSearchOpenButtonTestId
          || targetElement.attr('data-tb-test-id') === vizHeaderSearchButtonImgTestId;
        if (isTextBox || isButton && $scope.searchOpen) {
          return;
        }
      }
      this.$scope.clearSearch();
      this.updateResults();
      if (isCloseIcon || isInternallyTriggered) {
        // if the close icon was activated with the keyboard or clicked, we want to close the omnibox and return focus
        // to the search glass button
        this.$scope.closed({event: $event});
      } else {
        // if something else triggered closing the omnibox by a keyboard press, then we want to close the
        // omnibox without resetting focus to the search glass button
        this.$scope.closedWithoutNeedingFocus({event: $event});
      }
    };

    this.$scope.$on('destroy', () => {
      // sometimes search results return after we have navigated away (usually to the search space). We don't
      // want to show the results omnibox without the search box because it looks weird, so we mark that the
      // search box is unmounting and know to ignore the returned results.
      this.isUnmountingOmni = true;
    });

    this.$scope.createItemId = (type: string, index: number) => {
      return `${type}-${index}`;
    };

    this.$scope.selectedId = () => {
      if (this.$scope.showNoResults || this.$scope.showResults || this.$scope.showPending) {
        if (this.selectedIndex === 0) return 'global';

        let type = '';
        if (this.selectedIndex < this.startIndexForType['metrics']) type = 'views';
        else if (this.selectedIndex < this.startIndexForType['workbooks']) type = 'metrics';
        else if (this.selectedIndex < this.startIndexForType['datasources']) type = 'workbooks';
        else if (this.selectedIndex < this.startIndexForType['flows']) type = 'datasources';
        else if (this.selectedIndex < this.startIndexForType['projects']) type = 'flows';
        else if (this.selectedIndex < this.startIndexForType['tables']) type = 'projects';
        else if (this.selectedIndex < this.startIndexForType['databases']) type = 'tables';
        else if (this.selectedIndex < this.startIndexForType['dataRoles']) type = 'databases';
        else if (this.selectedIndex < this.startIndexForType['users']) type = 'dataRoles';
        else type = 'users';

        return `${type}-${this.selectedIndex - this.startIndexForType[type]}`;
      }
      return '';
    };

    this.$scope.totalNumberOfSections = (): number => {
      if (this.$scope.showNoResults || this.$scope.showResults || this.$scope.showPending) {
        let numSections = 0;
        if (FeatureFlags.searchSpace) numSections++;
        if (!_.isUndefined(this.$scope.views) && this.$scope.views.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.metrics) && this.$scope.metrics.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.workbooks) && this.$scope.workbooks.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.projects) && this.$scope.projects.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.flows) && this.$scope.flows.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.datasources) && this.$scope.datasources.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.dataRoles) && this.$scope.dataRoles.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.databases) && this.$scope.databases.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.tables) && this.$scope.tables.total > 0) numSections++;
        if (!_.isUndefined(this.$scope.users) && this.$scope.users.total > 0) numSections++;

        return numSections;
      }

      return 0;
    };

    this.$scope.keydown = ($event: JQueryKeyEventObject) => {
      switch ($event.which) {
        case Keys.ArrowUp:
          if (this.selectedIndex > 0) {
            this.selectedIndex--;
          } else {
            this.selectedIndex = this.maxSelectedIndex;
          }
          this.scrollSelectedItemIntoView();
          break;
        case Keys.ArrowDown:
          if (this.selectedIndex < this.maxSelectedIndex) {
            this.selectedIndex++;
          } else {
            this.selectedIndex = Math.min(0, this.maxSelectedIndex);
          }
          this.scrollSelectedItemIntoView();
          break;
        case Keys.Enter:
          if (this.selectedIndex >= 0) {

            const selectedElement = $('.tb-omnibox-results, .tb-omnibox-pending').find('.tb-selected');
            if (_.isUndefined(selectedElement) || _.isUndefined(selectedElement[0]) || selectedElement.hasClass('tb-disabled')) {
              this.isUnmountingOmni = true;
              const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });
              const path = urls.searchViewsUrl() + '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(this.$scope.searchText);
              Location.instance.setUrl(path);
              this.$scope.close(true); // want to return focus to search bar so use close()
              break;
            }

            let dataset = selectedElement[0].dataset;
            this.$scope.sendTelemetryEvent(
              dataset.type,
              Boolean(dataset.seeAll),
              Number(dataset.index),
              Number(dataset.total));


            let href = selectedElement.attr('href');
            Location.instance.setUrl(href);
            this.$scope.close(true); // want to return focus to search bar so use close()

          } else {  // enter inside search box, not inside omnibox
            if (FeatureFlags.searchSpace) {
              this.isUnmountingOmni = true;
              const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });
              const path = urls.searchViewsUrl() + '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(this.$scope.searchText);
              Location.instance.setUrl(path);
              this.$scope.close(true); // want to return focus to search bar so use close()
            }
          }

          break;
        case Keys.Escape:
          this.$scope.close(true); // want to return focus to search bar so use close()
          break;
      }
    };

    this.$scope.isItemSelected = (type: string, index: number): boolean => {
      return this.startIndexForType[type] + index === this.selectedIndex;
    };

    this.$scope.seeAllResultsMessage = (): string => {
      return Messages.omniboxSeeAllResults({ SEARCH_TERM: this.$scope.searchText });
    };

    this.$scope.sendTelemetryEvent = (type: string, seeAll: boolean, index: number, total: number): void => {
      this.telemetry.sendEvent<SearchResultClickedEvent>({
        type: TelemetryEvents.SearchResultClicked,
        eventSource: 'omni',
        resultType: type,
        seeAll: seeAll,
        index: index,
        total: total,
        searchTerm: this.$scope.searchText
      });
    };

    this.$scope.selectItem = (type: string, index: number): void => {
      if (!type || !_.isNumber(index)) return;
      this.selectedIndex = this.startIndexForType[type] + index;
    };

    this.$scope.itemHref = (type: string, params: any): string => {
      const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });

      let href = '#';
      if (type === 'datasource') {
        href = urls.datasourceUrl(params.id);
      } else if (type === 'flow') {
        href = urls.flowUrl(params.id);
      } else if (type === 'project') {
        href = urls.projectUrl(params.id);
      } else if (type === 'view') {
        href = urls.viewUrl(params.path);
      } else if (type === 'workbook') {
        href = urls.workbookUrl(params.id);
      } else if (type === 'siteUser') {
        href = urls.userUrl(params.username, params.domainName);
      } else if (type === 'table') {
        href = urls.catalogTableUrl(params.id);
      } else if (type === 'database') {
        href = urls.catalogDatabaseUrl(params.id);
      } else if (type === 'dataRole') {
        href = urls.dataRoleUrl(params.id);
      } else if (type === 'metric') {  // double check this
        href = urls.metricUrl(params.id);
      }

      return href;
    };

    const andOrderByRelevance = '&' + UrlParamKeys.Order + '=' + Order.serialize([Order.desc(OrderKeys.relevance)]);

    this.$scope.seeAllHref = (type: string): string => {

      // get the base url
      let seeAllHref =  this.seeAllUrlForType(type);

      // Add the search query to the url
      seeAllHref += '?' + ContentFiltersKeys.search + '=' + encodeURIComponent(this.$scope.searchText);

      if (!FeatureFlags.searchSpace) {
        if (type === 'views' || type === 'workbooks' || type === 'datasources') {
          seeAllHref += andOrderByRelevance;
        }
      }
      return seeAllHref;
    };

    this.$scope.showSeeAll = (list: ListResult<any>, type: string): boolean => {
      return type !== 'users' || server.isSessionSiteOrServerAdmin();
    };
    this.$scope.tooltipForDataSource = (datasource: IDataSourceInfo): string => {
      return this.$translate.instant('itemWithProject', { ITEM: datasource.name, PROJECT: datasource.project.name });
    };
    this.$scope.tooltipForView = (view: IViewInfo): string => {
      return this.$translate.instant('viewWithWorkbook', { VIEW: view.name, WORKBOOK: view.workbook.name });
    };
    this.$scope.tooltipForWorkbook = (workbook: IWorkbookInfo): string => {
      return this.$translate.instant('itemWithProject', { ITEM: workbook.name, PROJECT: workbook.project.name });
    };
    this.$scope.tooltipForFlow = (flow: FlowListItemInfo): string => {
      return this.$translate.instant('itemWithProject', { ITEM: flow.name, PROJECT: flow.project.name });
    };
    this.$scope.maxResultsPerType = OmniboxCtrl.MaxResultsPerType;

    const windowElement = $(this.$window);
    this.$scope.popupMaxHeight = () => {
      return windowElement.height() - OmniboxCtrl.SpaceLeftWhenMaxHeight;
    };

    this.$scope.clearSearch = () => this.$scope.searchText = '';

    this.$scope.textBoxName = OmniboxCtrl.TextBoxName;

    this.$scope.userProfilePictureSize = ProfilePictureStyles.Sizes.SmallIconSize;

    this.$scope.userProfilePictureFontSize = ProfilePictureStyles.FontSizes.SmallIconFontSize;

    this.$scope.activitySpinnerProps = {
      testId: 'omnibox-pending-spinner',
      spinnerSize: ActivitySpinnerSize.Small
    };

  }

  // the type string parameter is hardcoded in the omniboxContentAnalytics.pug
  private seeAllUrlForType(type: string) {
    if (FeatureFlags.searchSpace) {
      const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });
      if (type === 'datasources') {
        return urls.searchDatasourcesUrl();
      } else if (type === 'flows') {
        return urls.searchFlowsUrl();
      } else if (type === 'projects') {
        return urls.searchProjectsUrl();
      } else if (type === 'views' || type === 'global') {
        return urls.searchViewsUrl();
      } else if (type === 'workbooks') {
        return urls.searchWorkbooksUrl();
      } else if (type === 'users') {
        // users don't show up in search space
        return urls.usersUrl();
      } else if (type === 'tables') {
        return urls.searchTablesUrl();
      } else if (type === 'databases') {
        return urls.searchDatabasesUrl();
      } else if (type === 'dataRoles') {
        return urls.searchDataRolesUrl();
      } else if (type === 'metrics') {
        return urls.searchMetricsUrl();
      }

      return urls.searchViewsUrl();

    } else {
      return this.seeAllUrlForType_NoSearchSpace(type);
    }
  }

  // delete with searchSpace feature flag
  private seeAllUrlForType_NoSearchSpace(type: string) {
    const urls = new SiteUrls({ siteUrlName: this.server.sessionSiteUrlName(), queryString: window.location.search });

    if (type === 'datasources') {
      return urls.datasourcesUrl();
    } else if (type === 'flows') {
      return urls.flowsUrl();
    } else if (type === 'projects') {
      return urls.projectsUrl();
    } else if (type === 'views') {
      return urls.viewsUrl();
    } else if (type === 'workbooks') {
      return urls.workbooksUrl();
    } else if (type === 'users') {
      // users don't show up in search space
      return urls.usersUrl();
    } else if (type === 'tables') {
      return urls.catalogTablesUrl();
    } else if (type === 'databases') {
      return urls.catalogDatabasesUrl();
    } else if (type === 'dataRoles') {
      return urls.dataRolesUrl();
    } else if (type === 'metrics') {
      return urls.metricsUrl();
    }
    return urls.projectsUrl();
  }

  private updateAriaLiveText(totalResults: number) {
    const ariaLiveRegion = $('.tb-omnibox-status');
    let text = '';
    if (ariaLiveRegion) {
      if (this.$scope.showPending) {
        text = Messages.omniboxFetchingResults();
      } else if (this.$scope.showNoResults) {
        text = Messages.omniboxShowingNoResults();
      } else if(this.$scope.showResults) {
        text = Messages.omniboxShowingResults({
          TOTAL_COUNT: totalResults,
          TOTAL_COUNT_LOC: Formatters.formatCount(totalResults)
        });
      }

      ariaLiveRegion.text(text);
    }
  }

  private updateResults() {
    if (!!this.$scope.searchText) {
      const requestProgress = {
        pendingRequests: this.getPendingRequestsCount(),
        totalResults: 0
      };
      this.currentRequestProgress = requestProgress;

      // if already showing results, don't add the spinner (e.g., if the omnibox is open as the user types)
      // but if the omnibox isn't open yet, show a spinner while we fetch results
      this.$scope.showPending = this.$scope.showResults || this.$scope.showNoResults ? false : true;

      if (this.$scope.showPending) {
        this.updateAriaLiveText(0);
      }

      // This will close the search panel when clicking on an iframe
      // (NLP tab and the viz) because window will lose focus when clicking into the iframe
      setTimeout(() => {
        window.addEventListener('blur', this.onWindowBlur);
      });

      const updateProgress = (result: ListResult<any>) => {
        if (this.currentRequestProgress === requestProgress && !this.isUnmountingOmni) {
          requestProgress.totalResults += result.total;
          requestProgress.pendingRequests--;
          if (requestProgress.pendingRequests === 0) {

            this.currentRequestProgress = null;
            this.$scope.showResults = (requestProgress.totalResults > 0);
            this.$scope.showNoResults = !this.$scope.showResults;
            this.$scope.showPending = false;
            this.$scope.showCatalogingError = this.$scope.tablesReturnedError || this.$scope.databasesReturnedError;

            this.updateStartIndexes();
            // we want the index to be -1 if we're not showing results so we don't hit enter on something that isn't showing
            // (we don't highlight the 'no results' text)
            this.selectedIndex = FeatureFlags.searchSpace && (this.$scope.showResults || this.$scope.showPending) ? 0 : -1;
            this.maxSelectedIndex = requestProgress.totalResults > 0
              ? this.startIndexForType['users'] + this.itemsForType(this.$scope.users) - 1
              : -1;

            this.updateAriaLiveText(requestProgress.totalResults);

            // make sure we rerender the results
            this.$scope.$applyAsync();
          }
        }
      };
      this.updateType<IProjectInfo>(
        this.projects.all(),
        sortOptionsBySearchSubspace['projects'].defaultOrder,
        (result: ListResult<IProjectInfo>) => {
          this.$scope.projects = result;
          updateProgress(result);
        }
      );
      if(!this.server.isLimitedUserVisibilityEnforced()) {
        this.updateType<IUserWithPublicInfo>(
          this.users.all().withExtraParams(OmniboxCtrl.GetUsersExtraParams),
          [Order.asc(OrderKeys.displayName)],
          (result: ListResult<IUserWithPublicInfo>) => {
            this.$scope.users = result;
            updateProgress(result);
          }
        );
      }
      this.updateType<IViewInfo>(
        this.views.all(),
        sortOptionsBySearchSubspace['views'].defaultOrder,
        (result: ListResult<IViewInfo>) => {
          this.$scope.views = result;
          updateProgress(result);
        }
      );
      if (FeatureFlags.metricsSpaceEnabled) {
        this.updateType<IMetricInfoForOmni>(
          this.metrics.all(),
          sortOptionsBySearchSubspace['metrics'].defaultOrder,
          (result: ListResult<IMetricInfo>) => {
            this.$scope.metrics = result;
            updateProgress(result);

            if (result.total > 0) {
              this.getMetricValues(result);
            }
          }
        );
      }
      this.updateType<IWorkbookInfo>(
        this.workbooks.all(),
        sortOptionsBySearchSubspace['workbooks'].defaultOrder,
        (result: ListResult<IWorkbookInfo>) => {
          this.$scope.workbooks = result;
          updateProgress(result);
        }
      );
      this.updateType<IDataSourceInfo>(
        this.datasources.published(),
        sortOptionsBySearchSubspace['datasources'].defaultOrder,
        (result: ListResult<IDataSourceInfo>) => {
          this.$scope.datasources = result;
          updateProgress(result);
        }
      );
      if (this.server.flowsOnSiteEnabled()) {
        this.updateType<FlowListItemInfo>(
          this.flows.all(),
          sortOptionsBySearchSubspace['flows'].defaultOrder,
          (result: ListResult<FlowListItemInfo>) => {
            this.$scope.flows = result;
            updateProgress(result);
          }
        );
      }
      if (this.server.catalogingOnSiteEnabled()) {
        const tablesResourceQuery = this.tables.all();
        tablesResourceQuery
          .addFilter(Filter.search(this.$scope.searchText))
          .getSlice(0, OmniboxCtrl.MaxResultsPerType)
          .then((result: ListResult<DatabaseTablesDisplayGridRowData>) => {
            this.$scope.tablesReturnedError = false;
            this.$scope.tables = mapListResult(result, mapCatalogItem);
            updateProgress(result);

          }).catch((reason: any) => {
            // Data Catalog might down, indexing, ingesting, etc. so just show 0 results and an error message.
            const emptyList = ListResult.empty<CatalogOmniboxItem>();
            this.$scope.tables = emptyList;
            this.$scope.tablesReturnedError = true;

            updateProgress(emptyList);
          });

        const databasesResourceQuery = this.databases.all();

        databasesResourceQuery
          .addFilter(Filter.search(this.$scope.searchText))
          .getSlice(0, OmniboxCtrl.MaxResultsPerType)
          .then((result: ListResult<DatabasesDisplayGridRowData>) => {
            this.$scope.databasesReturnedError = false;
            this.$scope.databases = mapListResult(result, mapCatalogItem);
            updateProgress(result);

          }).catch((reason: any) => {
            // Data Catalog might down, indexing, ingesting, etc. so just show 0 results and an error message.
            const emptyList = ListResult.empty<CatalogOmniboxItem>();
            this.$scope.databases = emptyList;
            this.$scope.databasesReturnedError = true;

            updateProgress(emptyList);
          });
      }

      this.updateType<IDataRoleInfo>(
        this.dataRoles.all(),
        sortOptionsBySearchSubspace['dataRoles'].defaultOrder,
        (result: ListResult<IDataRoleInfo>) => {
          this.$scope.dataRoles = result;
          updateProgress(result);
        }
      );
    } else {
      this.currentRequestProgress = undefined;

      this.$scope.datasources = ListResult.empty<IDataSourceInfo>();
      this.$scope.flows = ListResult.empty<FlowListItemInfo>();
      this.$scope.projects = ListResult.empty<IProjectInfo>();
      this.$scope.users = ListResult.empty<IUserWithPublicInfo>();
      this.$scope.views = ListResult.empty<IViewInfo>();
      this.$scope.metrics = ListResult.empty<IMetricInfoForOmni>();
      this.$scope.workbooks = ListResult.empty<IWorkbookInfo>();
      this.$scope.tables = ListResult.empty<CatalogOmniboxItem>();
      this.$scope.databases = ListResult.empty<CatalogOmniboxItem>();
      this.$scope.dataRoles = ListResult.empty<IDataRoleInfo>();
      this.$scope.showResults = false;
      this.$scope.showNoResults = false;
      this.$scope.showPending = false;
      this.$scope.showCatalogingError = false;
      this.updateStartIndexes();
      this.selectedIndex = FeatureFlags.searchSpace? 0 : -1;
      this.maxSelectedIndex = FeatureFlags.searchSpace? 0 : -1;
    }
  }

  private getMetricValues(result: ListResult<IMetricInfo>) {
    const ids: string[] = result.items.map((item: IMetricInfo) => item.id);

    this.metricDataPointsResource.getMetricDataPoints(ids)
      .then((metricDataList: IMetricDataList) => {

        for (let index in this.$scope.metrics.items) {
          let metric: IMetricInfoForOmni = this.$scope.metrics.items[index];
          const data = metricDataList.data[metric.id];

          if (!!data) {
            metric = getMergedMetricInfo(metric, metricDataList);

            // update metric in array
            this.$scope.metrics.items[index] = metric;
          } // otherwise, no need to update
        }
      });
  }

  private getPendingRequestsCount() {
    let total = 5;
    if (this.server.catalogingOnSiteEnabled()) {
      total = total + 2;  // 1 for tables, 1 more for databases
    }

    if (this.server.flowsOnSiteEnabled()) {
      total = total + 1;
    }

    if (!this.server.isLimitedUserVisibilityEnforced()) {
      total = total + 1;  // users call to happen only if limited user visbility is not enforced
    }

    if (FeatureFlags.metricsSpaceEnabled) {
      total = total + 1;
    }

    return total;
  }

  private scrollSelectedItemIntoView() {
    if (this.selectedIndex < 0) {
      return;
    }

    const viewPortElement = $('.tb-omnibox-results, .tb-omnibox-pending');
    const selectedItemElement = $(viewPortElement.find('.tb-omnibox-menu-item, .tb-omnibox-section-header, .tb-omnibox-section-global-see-all')[this.selectedIndex]);

    // leave space for the header/margin at top and bottom of the list
    const menuItemHeight = selectedItemElement.height();
    const currentScroll = viewPortElement.scrollTop() - menuItemHeight;
    const viewPortTop = 0;
    const viewPortLength = viewPortElement.height() - menuItemHeight - menuItemHeight;

    const itemTop = selectedItemElement.position().top;
    const itemBottom = itemTop + menuItemHeight;

    if (itemTop < viewPortTop) {
      viewPortElement.scrollTop(currentScroll + itemTop);
    } else if (itemBottom > viewPortLength) {
      viewPortElement.scrollTop(currentScroll + itemBottom - viewPortLength);
    }
  }

  private updateType<T>(query: ResourceQuery<T>, order: IOrderClause[], updateFn: (result: ListResult<T>) => any) {
    query
      .addFilter(Filter.search(this.$scope.searchText))
      .withOrder(order)
      .getSlice(0, OmniboxCtrl.MaxResultsPerType)
      .then((result: ListResult<T>) => {
        updateFn(result);
      });
  }

  private updateStartIndexes() {
    if (FeatureFlags.searchSpace) {
      this.startIndexForType['global'] = 0;
      this.startIndexForType['views'] = 1;
    } else {
      this.startIndexForType['views'] = 0;
    }
    this.startIndexForType['metrics'] = this.startIndexForType['views'] + this.itemsForType(this.$scope.views);
    this.startIndexForType['workbooks'] = this.startIndexForType['metrics'] + this.itemsForType(this.$scope.metrics);
    this.startIndexForType['datasources'] = this.startIndexForType['workbooks'] + this.itemsForType(this.$scope.workbooks);
    this.startIndexForType['flows'] = this.startIndexForType['datasources'] + this.itemsForType(this.$scope.datasources);
    this.startIndexForType['projects'] = this.startIndexForType['flows'] + this.itemsForType(this.$scope.flows);
    this.startIndexForType['tables'] = this.startIndexForType['projects'] + this.itemsForType(this.$scope.projects);
    this.startIndexForType['databases'] = this.startIndexForType['tables'] + this.itemsForType(this.$scope.tables);
    this.startIndexForType['dataRoles'] = this.startIndexForType['databases'] + this.itemsForType(this.$scope.databases);
    this.startIndexForType['users'] = this.startIndexForType['dataRoles'] + this.itemsForType(this.$scope.dataRoles);
  }

  private itemsForType(list: ListResult<any> | null | undefined): number {
    if (list) {
      let items = list.items ? list.items.length : 0;
      if (items > 0) {
        items++;  // add one for the section header
      }
      return items;
    } else {
      return 0;
    }
  }

  private onWindowBlur = () => {
    window.removeEventListener('blur', this.onWindowBlur);
    this.$scope.close(false);
  }
}

Module.instance().controller('OmniboxCtrl', OmniboxCtrl);
